import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { FC, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { vars } from '../../../reactive';
import { getKeys, sentenceCase } from '../../../utils';
import FormContainer, { PreviousPage } from '../../Shared/FormContainer/FormContainer';
import { Line } from '../../Shared/Shared';
import { FORM_INFO_COMPONENT_TYPES } from '../types';
import {
  FormFillContainer,
  FormFillTitle,
  FormInfoFillContainer,
  FormInfoWrapper,
  FormPetName,
  FormQuestionContainer,
  FormQuestionTitle,
  FormQuestionsContainer,
  FormQustionAnswer,
  PetFormAnswers
} from './styled';

interface FormInfoFormFillSummaryProps {
  type: keyof typeof FORM_INFO_COMPONENT_TYPES;
  formOptions: ReturnType<typeof useForm>;
  onSubmit: () => void;
  nextButtonText: string;
  previousPage?: PreviousPage;
  privacy_policy_url?: string;
  branchName?: string;
  terms_and_conditions_url?: string;
}

const FormInfoFormFillSummary: FC<FormInfoFormFillSummaryProps> = ({ type, onSubmit, previousPage, nextButtonText, privacy_policy_url, branchName, terms_and_conditions_url }) => {
  const { t } = useTranslation();
  const selectedForms = useReactiveVar(vars.selectedForms);
  const selectedPets = useReactiveVar(vars.selectedPets);
  const formAnswers = useReactiveVar(vars.formsAnswers);
  const breeds = useReactiveVar(vars.breeds);
  const title = t(FORM_INFO_COMPONENT_TYPES[type].title);

  const getAnswer = useCallback(
    (formId: string, questionId: string, petRecordId?: string) => {
      const answer = formAnswers?.find(answer => answer?.questionId === questionId && answer?.formId === formId);
      const question = selectedForms?.find(form => form?.id === formId)?.Questions?.find(question => question?.id === questionId);
      const petAnswer = answer?.answers?.find(answer => answer?.PetRecordId === petRecordId) || answer?.answers?.[0];
      let answerString =
        petAnswer?.answer ||
        question?.AnswerOptions?.filter(option => petAnswer?.AnswerOptionId?.includes(option?.id))
          ?.map(option => option?.title)
          ?.join(', ') ||
        '';
      if (answerString === 'true') {
        answerString = t('formFillSummary.answerYes');
      }
      if (answerString === 'false') {
        answerString = t('formFillSummary.answerNo');
      }

      if (question?.get_answers_from === 'CHRONIC_CONDITIONS') {
        const parsedAnswer = JSON.parse(petAnswer?.answer || '{}');
        const conditions = getKeys(parsedAnswer || {})
          .filter(cond => parsedAnswer[cond])
          .map(cond => sentenceCase(cond || ''))
          .join(', ');
        answerString = conditions;
      }

      if (question?.get_answers_from === 'VET' || question?.get_answers_from === 'INSURER') {
        const parsedAnswer = JSON.parse(petAnswer?.answer || '{}');
        answerString = `${parsedAnswer?.name}, ${parsedAnswer?.country || ''} ${parsedAnswer?.city || ''} ${parsedAnswer?.line1 || ''} ${parsedAnswer?.line2 || ''}`;
      }

      if (question?.get_answers_from === 'BREED') {
        answerString = (breeds?.find(breed => breed?.id === petAnswer?.answer)?.name as string) || '';
      }

      if (question?.type === 'ADDRESS') {
        const parsedAnswer = JSON.parse(petAnswer?.answer || '[{}]')?.[0];
        answerString = `${parsedAnswer?.country || ''} ${parsedAnswer?.city || ''} ${parsedAnswer?.line1 || ''} ${parsedAnswer?.line2 || ''}`;
      }

      if (question?.type === 'PHONE') {
        const parsedAnswer = JSON.parse(petAnswer?.answer || '{}');
        answerString = `${parsedAnswer?.country_code || ''} ${parsedAnswer?.number || ''}`;
      }

      return answerString;
    },
    [formAnswers, selectedForms, breeds]
  );

  return (
    <FormInfoWrapper>
      <FormContainer title={title} previousPage={previousPage} onSubmit={onSubmit} nextButtonText={nextButtonText}>
        <FormInfoFillContainer>
          {selectedForms?.map((form, formIndex) => {
            const nonPetQuestions = form?.Questions?.filter(question => !question?.ask_per_pet);
            const petQuestions = form?.Questions?.filter(question => question?.ask_per_pet);
            return (
              <FormFillContainer key={form?.id}>
                <FormFillTitle>{form?.name}</FormFillTitle>
                {!!nonPetQuestions?.length && (
                  <FormQuestionsContainer>
                    {nonPetQuestions.map(question => (
                      <FormQuestionContainer key={question?.id}>
                        <FormQuestionTitle>{question?.title}</FormQuestionTitle>
                        <FormQustionAnswer>{getAnswer(form?.id, question?.id, '')}</FormQustionAnswer>
                      </FormQuestionContainer>
                    ))}
                  </FormQuestionsContainer>
                )}
                {!!petQuestions?.length && !!nonPetQuestions?.length && <Line />}
                {!!petQuestions?.length &&
                  selectedPets?.map((pet, petIndex) => (
                    <PetFormAnswers key={pet?.id}>
                      <FormPetName>{t('formFillSummary.aboutPet', { name: pet?.name })}</FormPetName>
                      <FormQuestionsContainer>
                        {petQuestions?.map(question => (
                          <FormQuestionContainer key={question?.id}>
                            <FormQuestionTitle>{question?.title}</FormQuestionTitle>
                            <FormQustionAnswer>{getAnswer(form?.id, question?.id, pet?.PetRecord?.id)}</FormQustionAnswer>
                          </FormQuestionContainer>
                        ))}
                      </FormQuestionsContainer>
                      {petIndex !== selectedPets.length - 1 && <Line />}
                    </PetFormAnswers>
                  ))}
                {formIndex !== selectedForms.length - 1 && <Line />}
              </FormFillContainer>
            );
          })}
        </FormInfoFillContainer>
        <span style={{ display: 'flex', fontSize: 14, marginBottom: 20, marginTop: 20, gap: 4 }}>
          {t('formFillSummary.agreementPrefix', { branchName })}{' '}
          {!!terms_and_conditions_url && (
            <a href={terms_and_conditions_url} target="_blank" style={{ textDecoration: 'underline' }}>
              {t('formFillSummary.termsLink')}
            </a>
          )}
          {!terms_and_conditions_url && t('formFillSummary.termsText')} {t('formFillSummary.agreementConnector')}{' '}
          {!!privacy_policy_url && (
            <a href={privacy_policy_url} target="_blank" style={{ textDecoration: 'underline' }}>
              {t('formFillSummary.privacyLink')}
            </a>
          )}
          {!privacy_policy_url && t('formFillSummary.privacyText')}
        </span>
      </FormContainer>
    </FormInfoWrapper>
  );
};

export default FormInfoFormFillSummary;
